// The policy DSL. A rule is data (a small AST), so the same policy can be evaluated three ways:
// `evaluate` against a concrete resource, `possible` against no resource (route and menu gates),
// and `scopeOf` to derive the filter a list query must apply up front (never post-filter).
// ADR-0008 records why policies are data rather than functions.
import { grantedOffices, hasRole, isPlatformAdmin, tenantGrant, } from '../principal.js';
import { attribute } from '../resources.js';
export const always = { op: 'always' };
export const never = { op: 'never' };
export const anyOf = (...rules) => ({ op: 'anyOf', rules });
export const allOf = (...rules) => ({ op: 'allOf', rules });
export const not = (rule) => ({ op: 'not', rule });
export const isRole = (...roles) => ({ op: 'isRole', roles });
export const platformAdmin = { op: 'isPlatformAdmin' };
export const isOwner = (attr) => ({ op: 'isOwner', attr });
export const isSelf = (attr) => ({ op: 'isSelf', attr });
export const hasOfficeGrantOn = (attr) => ({
    op: 'hasOfficeGrantOn',
    attr,
});
export const hasTenantGrant = { op: 'hasTenantGrant' };
export const attrIsNull = (attr) => ({ op: 'attrIsNull', attr });
/** Any role in the current tenant. */
export const isMember = isRole('TenantAdmin', 'Manager', 'Agent');
/**
 * Concrete decision: the resource is known. Roles, ownership, self and office grants are
 * tenant-local: they count only when the principal acts in the resource's own tenant. A visitor
 * admitted by a `tenant#` grant is judged by grant-aware rules alone.
 */
export function evaluate(rule, principal, resource) {
    const local = principal.tenantId !== undefined && principal.tenantId === resource.tenantId;
    return evaluateIn(rule, principal, resource, local);
}
function evaluateIn(rule, principal, resource, local) {
    switch (rule.op) {
        case 'always':
            return true;
        case 'never':
            return false;
        case 'anyOf':
            return rule.rules.some((r) => evaluateIn(r, principal, resource, local));
        case 'allOf':
            return rule.rules.every((r) => evaluateIn(r, principal, resource, local));
        case 'not':
            return !evaluateIn(rule.rule, principal, resource, local);
        case 'isRole':
            return local && hasRole(principal, ...rule.roles);
        case 'isPlatformAdmin':
            return isPlatformAdmin(principal);
        case 'isOwner': {
            const value = attribute(resource, rule.attr);
            return (local &&
                principal.agentCode !== undefined &&
                value !== null &&
                value !== undefined &&
                value === principal.agentCode);
        }
        case 'isSelf': {
            const value = attribute(resource, rule.attr);
            return local && value !== null && value !== undefined && value === principal.sub;
        }
        case 'hasOfficeGrantOn': {
            const value = attribute(resource, rule.attr);
            return (local && value !== null && value !== undefined && principal.grants.has(`office#${value}`));
        }
        case 'hasTenantGrant':
            return (resource.tenantId !== undefined && principal.grants.has(tenantGrant(resource.tenantId)));
        case 'attrIsNull': {
            const value = attribute(resource, rule.attr);
            return value === null || value === undefined;
        }
    }
}
/**
 * Could this principal ever satisfy the rule for *some* resource? Used for route and menu gates,
 * where no resource has been loaded yet. Resource-dependent predicates answer with what the
 * principal brings: an owner check needs an agent code, an office-grant check needs a grant.
 * With `visitor` set the principal is not acting in its own tenant (a platform support session
 * on a tenant route), so tenant-local predicates cannot hold.
 */
export function possible(rule, principal, visitor = false) {
    switch (rule.op) {
        case 'always':
            return true;
        case 'never':
            return false;
        case 'anyOf':
            return rule.rules.some((r) => possible(r, principal, visitor));
        case 'allOf':
            return rule.rules.every((r) => possible(r, principal, visitor));
        case 'not':
            // The negated predicate might hold for some resource and not another; only a rule that is
            // certain in itself makes the negation impossible. Conservative: assume it can be satisfied.
            return !isCertain(rule.rule, principal, visitor);
        case 'isRole':
            return !visitor && hasRole(principal, ...rule.roles);
        case 'isPlatformAdmin':
            return isPlatformAdmin(principal);
        case 'isOwner':
            return !visitor && principal.agentCode !== undefined;
        case 'isSelf':
            return !visitor;
        case 'hasOfficeGrantOn':
            return !visitor && grantedOffices(principal).length > 0;
        case 'hasTenantGrant':
            return [...principal.grants].some((g) => g.startsWith('tenant#'));
        case 'attrIsNull':
            return true;
    }
}
/** True when the rule holds for every resource given this principal (resource-independent). */
function isCertain(rule, principal, visitor) {
    switch (rule.op) {
        case 'always':
            return true;
        case 'never':
            return false;
        case 'anyOf':
            return rule.rules.some((r) => isCertain(r, principal, visitor));
        case 'allOf':
            return rule.rules.every((r) => isCertain(r, principal, visitor));
        case 'not':
            return !possible(rule.rule, principal, visitor);
        case 'isRole':
            return !visitor && hasRole(principal, ...rule.roles);
        case 'isPlatformAdmin':
            return isPlatformAdmin(principal);
        default:
            return false;
    }
}
const NONE = { kind: 'none' };
const ALL = { kind: 'all' };
const UNSCOPABLE = { kind: 'unscopable' };
/**
 * Derive the list scope from a rule. `tenantId` is the tenant being listed (the principal's own
 * unless a tenant grant is being exercised). Rules that compare against a specific record
 * (`isSelf`) or negate a resource predicate are not scopable; a test pins that no shipped list
 * policy hits that case.
 */
export function scopeOf(rule, principal, tenantId) {
    const local = principal.tenantId === tenantId;
    switch (rule.op) {
        case 'always':
            return ALL;
        case 'never':
            return NONE;
        case 'anyOf':
            return rule.rules.map((r) => scopeOf(r, principal, tenantId)).reduce(union, NONE);
        case 'allOf':
            return rule.rules.map((r) => scopeOf(r, principal, tenantId)).reduce(intersection, ALL);
        case 'not':
            return isCertain(rule.rule, principal, !local)
                ? NONE
                : possible(rule.rule, principal, !local)
                    ? UNSCOPABLE
                    : ALL;
        case 'isRole':
            return local && hasRole(principal, ...rule.roles) ? ALL : NONE;
        case 'isPlatformAdmin':
            return isPlatformAdmin(principal) ? ALL : NONE;
        case 'isOwner':
            return !local || principal.agentCode === undefined
                ? NONE
                : { kind: 'some', offices: new Set(), agentCode: principal.agentCode, officeless: false };
        case 'isSelf':
            return UNSCOPABLE;
        case 'hasOfficeGrantOn': {
            const offices = local ? grantedOffices(principal) : [];
            return offices.length === 0
                ? NONE
                : { kind: 'some', offices: new Set(offices), officeless: false };
        }
        case 'hasTenantGrant':
            return principal.grants.has(tenantGrant(tenantId)) ? ALL : NONE;
        case 'attrIsNull':
            return { kind: 'some', offices: new Set(), officeless: true };
    }
}
function union(a, b) {
    if (a.kind === 'unscopable' || b.kind === 'unscopable')
        return UNSCOPABLE;
    if (a.kind === 'all' || b.kind === 'all')
        return ALL;
    if (a.kind === 'none')
        return b;
    if (b.kind === 'none')
        return a;
    const agentCode = a.agentCode ?? b.agentCode;
    return {
        kind: 'some',
        offices: new Set([...a.offices, ...b.offices]),
        ...(agentCode !== undefined ? { agentCode } : {}),
        officeless: a.officeless || b.officeless,
    };
}
function intersection(a, b) {
    if (a.kind === 'unscopable' || b.kind === 'unscopable')
        return UNSCOPABLE;
    if (a.kind === 'none' || b.kind === 'none')
        return NONE;
    if (a.kind === 'all')
        return b;
    if (b.kind === 'all')
        return a;
    // Each partial scope is an OR of up to three terms (offices, own agent, office-less rows).
    // Their AND is expressible only when no cross term survives: an agent term on one side with
    // an office or office-less term on the other would need "my rows inside those offices",
    // which the filter shape cannot say. Both sides agent-only is fine (same principal, same code).
    const agentOnly = (s) => s.agentCode !== undefined && s.offices.size === 0 && !s.officeless;
    const agentCode = a.agentCode ?? b.agentCode;
    if (agentCode !== undefined) {
        if (!(agentOnly(a) && agentOnly(b)))
            return UNSCOPABLE;
        return { kind: 'some', offices: new Set(), agentCode, officeless: false };
    }
    const offices = new Set([...a.offices].filter((o) => b.offices.has(o)));
    const officeless = a.officeless && b.officeless;
    if (offices.size === 0 && !officeless)
        return NONE;
    return { kind: 'some', offices, officeless };
}
//# sourceMappingURL=rules.js.map